import React, { useRef, useState } from 'react'
import { css } from '@emotion/core'
import { ArrowCaret } from '@fs/zion-icon'
import { arrayOf, number, string } from 'prop-types'

const cssCarousel = css`
  z-index: 1500;
  display: flex;
  justify-content: space-between;
  touch-action: manipulation;
  background-color: rgba(0, 0, 0, 0.75);
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
`
const cssArrow = css`
  position: relative;
  top: calc(50vh - 20px);
  opacity: 0.6;
  z-index: 10;
  &:hover {
    cursor: pointer;
    opacity: 1;
  }
  &:focus {
    outline: none;
  }
`

const cssImageSwipeContainer = css`
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
`

const cssImage = css`
  position: absolute;
  --thickness: 3px;
  display: block;
  height: auto;
  width: 100%;
  max-height: 90vh;
  min-width: 0;
  object-fit: contain;
  margin: 1.25% auto;
  background-color: gray;
`

export default function AncestorGallery({ imageList = [], index }) {
  const [imageIndex, setImageIndex] = useState(index)
  const imageRef = useRef()
  const swipeBuddyImageRef = useRef()
  const touchRef = useRef({
    startX: undefined,
    deltaX: undefined,
    startImageIndex: undefined,
  })

  const prevSlide = () => {
    let idx
    if (imageIndex === 0) {
      idx = imageList.length - 1
    } else {
      idx = imageIndex - 1
    }
    setImageIndex(idx)
  }

  const nextSlide = () => {
    let idx
    if (imageIndex === imageList.length - 1) {
      idx = 0
    } else {
      idx = imageIndex + 1
    }
    setImageIndex(idx)
  }

  const onTouchStartHandler = (e) => {
    if (e.touches.length > 1) {
      return
    }

    const { pageX } = e.touches[0]
    imageRef.current.style.transition = ''
    swipeBuddyImageRef.current.style.transition = ''

    // Hide next image initially to let touch position it
    swipeBuddyImageRef.current.style.display = ''

    touchRef.current.startX = pageX
    touchRef.current.imageLeftX = imageRef.current.getBoundingClientRect().left
    touchRef.current.imageWidth = imageRef.current.getBoundingClientRect().width
    touchRef.current.startImageIndex = imageIndex
  }

  const onTouchMoveHandler = (e) => {
    const { pageX } = e.touches[0]
    const deltaX = pageX - touchRef.current.startX
    const pastSwipeThreshold = Math.abs(deltaX / window.innerWidth) > 0.2
    touchRef.current.deltaX = deltaX
    touchRef.current.pastSwipeThreshold = pastSwipeThreshold

    const { startImageIndex } = touchRef.current

    if (pastSwipeThreshold && imageIndex === startImageIndex) {
      // Setting src to '' before loading new image prevents confusing image loading lag on slow networks
      imageRef.current.src = ''
      ;(deltaX < 0 ? nextSlide : prevSlide)()
    } else if (!pastSwipeThreshold && imageIndex !== startImageIndex) {
      imageRef.current.src = ''
      ;(deltaX < 0 ? prevSlide : nextSlide)()
    }

    const secondaryPos =
      deltaX < 0
        ? window.innerWidth + deltaX - touchRef.current.imageLeftX
        : -touchRef.current.imageLeftX - touchRef.current.imageWidth + deltaX
    const secondaryTransform = `translate(${secondaryPos}px)`
    const primaryTransform = `translate(${deltaX}px)`

    imageRef.current.style.transform = pastSwipeThreshold ? secondaryTransform : primaryTransform
    swipeBuddyImageRef.current.style.transform = pastSwipeThreshold ? primaryTransform : secondaryTransform

    let swipeBuddyIndex

    if (pastSwipeThreshold) {
      swipeBuddyIndex = startImageIndex
    } else if (deltaX < 0) {
      swipeBuddyIndex = (startImageIndex + 1) % imageList.length
    } else if (startImageIndex === 0) {
      swipeBuddyIndex = imageList.length - 1
    } else {
      swipeBuddyIndex = startImageIndex - 1
    }

    if (swipeBuddyImageRef.current.src !== imageList[swipeBuddyIndex]) {
      swipeBuddyImageRef.current.src = ''
      swipeBuddyImageRef.current.src = imageList[swipeBuddyIndex]
    }
  }

  const onTouchEndHandler = (e) => {
    if (e.touches?.length) {
      touchRef.current.startX = e.touches[0].pageX
      return
    }

    const imageTransition = 'transform 0.1s ease-out'
    imageRef.current.style.transition = imageTransition
    imageRef.current.style.transform = `translate(0px)`

    swipeBuddyImageRef.current.style.transition = imageTransition

    const nextImagePos =
      touchRef.current.deltaX < 0 !== touchRef.current.pastSwipeThreshold
        ? window.innerWidth - touchRef.current.imageLeftX
        : -touchRef.current.imageLeftX - touchRef.current.imageWidth

    swipeBuddyImageRef.current.style.transform = `translate(${nextImagePos}px)`
  }

  return (
    <div
      className="carousel"
      css={cssCarousel}
      onTouchStart={onTouchStartHandler}
      onTouchMove={onTouchMoveHandler}
      onTouchEnd={onTouchEndHandler}
    >
      <div
        onClick={prevSlide}
        css={cssArrow}
        onKeyPress={() => {
          prevSlide()
        }}
        role="button"
        tabIndex={0}
      >
        <ArrowCaret direction="left" size="xl" color="#fff" />
      </div>
      <div css={cssImageSwipeContainer}>
        <img
          ref={swipeBuddyImageRef}
          src={imageList[(imageIndex + 1) % (imageList.length - 1)]}
          alt="Ancestor"
          css={cssImage}
          style={{ display: 'none' }}
        />
        <img ref={imageRef} src={imageList[imageIndex]} alt="Ancestor" css={cssImage} />
      </div>
      <div
        onClick={nextSlide}
        css={cssArrow}
        onKeyPress={() => {
          nextSlide()
        }}
        role="button"
        tabIndex={-1}
      >
        <ArrowCaret direction="right" size="xl" color="#fff" />
      </div>
    </div>
  )
}

AncestorGallery.propTypes = {
  imageList: arrayOf(string),
  index: number,
}
